<template>
    <div class="mt-6 content">
        <h3 class="has-text-white"><i class="fad fa-fw fa-clipboard-list-check mr-3"></i>Record & Attendance Log</h3>

        <p class="mt-6 has-text-white" v-if="!registration.id"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading log...</p>

        <div v-else class="mt-6 pb-6" ref="top">
            <div class="columns is-gapless has-text-white" v-if="isPreRegistration">
                <div class="column is-narrow"><i class="far fa-fw fa-info-circle mr-2"></i></div>
                <div class="column">
                    <span class="has-text-weight-bold">You have pre-registered for this conference.</span><br>Additional information for this event may be made available to you as the event approaches.
                </div>
            </div>
            <div v-else>
                <ui-card class="pb-4 pl-4 pr-4">

                    <b-message type="is-warning" class="mt-5" v-if="stateRegistration.isLocked">
                        <div class="columns is-gapless">
                            <div class="column is-narrow"><i class="fas fa-exclamation-triangle fa-fw mr-3 has-text-warning"></i></div>
                            <div class="column">
                                <div class="is-size-6 has-text-weight-bold">Registration Locked</div>
                                <div class="is-size-7-mt-3">You have completed all available applications and forms, so no further changes to your registration are available.</div>
                            </div>
                        </div>
                    </b-message>

                    <section>
                        <div class="content mt-3 pb-3 mr-6">
                            <p>Below is your <strong><i class="far fa-clipboard-list-check mr-2 ml-1"></i>Record & Attendance Log</strong> used to record the sessions you attended, On-Demand Content watched, and other qualifying activities. This log represents the truthful and accurate account of the activities you completed for this conference.  It will be used to determine what certifications you quality for.</p>
                            <p>Add sessions from <router-link :to="{name: 'schedule'}"><strong><i class="far fa-calendar-circle-user mr-2 ml-1"></i>Sessions & Schedules</strong></router-link> or watch <router-link :to="{name: 'on-demand'}"><strong><i class="far fa-clapperboard-play mr-2 ml-1"></i>On-Demand Content</strong></router-link> to contribute to the log.</p>
                        </div>

                        <hr class=""/>

<!--                        <json-viewer :value="registration.reg_sessions"></json-viewer>-->
<!--                        <json-viewer :value="registration.reg_log_session_ids"></json-viewer>-->
<!--                        <json-viewer :value="registration.reg_record_log"></json-viewer>-->
<!--                        <json-viewer :value="logBuilder"></json-viewer>-->

                        <table class="table">
                            <template v-for="row in logBuilder">
                                <tr v-if="row.type === 'entry'" class="log-row">
                                    <template v-if="row.session && row.session.type === 'on-demand'">
                                        <td style="width: 10px;">
                                            <b-tag type="is-success" style="width: 100%; justify-content: left;"><i class="fas fa-check fa-fw mr-2"></i><strong class="has-text-white">{{ row.total_minutes }}</strong> Minute<span v-if="row.total_minutes !== 1">s</span></b-tag>
                                        </td>
                                        <td>
                                            <div class="log-title">{{ row.session.title }}</div>
                                            <div class="is-size-7 mt-2 has-text-grey"><i class="far fa-fw fa-clapperboard-play mr-2"></i>You watched this session on-demand. </div>

                                            <template v-if="row.children.length === 1">
                                                <div class="log-date">{{ displayDates(row.obj.start_at, row.obj.end_at) }}</div>
                                            </template>
                                            <template v-else>
                                                <div>
                                                    <div class="log-date is-clickable" @click="row.showRecords = !row.showRecords">
                                                        <i class="fas fa-fw mr-1" :class="{'fa-chevron-down': row.showRecords, 'fa-chevron-right': !row.showRecords}"></i>
                                                        {{ row.children.length }} Records
                                                    </div>

                                                    <div class="mb-5" v-if="row.showRecords">
                                                        <div class="log-date" v-for="record in row.children">
                                                            <i class="fas fa-fw mr-2"></i>{{ displayDates(record.start_at, record.end_at) }} ({{ record.minutes }} active min)
                                                            <span class="ml-3 has-text-warning is-clickable" @click="removeChildLog(row, record)"><i class="fas fa-fw fa-times mr-1"></i>Remove Entry</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                            <div v-if="row.hit_max" class="is-size-7 mb-2 has-text-grey"><i class="far fa-fw fa-check mr-2 has-text-success"></i>You have recorded the maximum amount of qualified time for this session.</div>
                                            <div v-else class="mt-3 mb-3">
                                                <b-progress class="session-progress mb-1 pb-0" size="is-small" type="is-primary" :value="row.total_minutes" :max="row.session.max_log_minutes"></b-progress>
                                                <div class="is-size-7 has-text-grey">You have recorded {{convertMinutesToHHMM(row.total_minutes)}}<span> of the maximum of {{convertMinutesToHHMM(row.session.max_log_minutes)}} qualified time</span>.</div>
                                            </div>
                                        </td>
                                        <td class="has-text-right" style="width: 10px;" v-if="row.children.length === 1">
                                            <b-button expanded outlined type="is-warning" @click="removeLog(row.obj)" :disabled="stateRegistration.isLocked">
                                                <i class="fas fa-fw fa-times mr-2"></i>Remove Entry
                                            </b-button>
                                        </td>
                                        <td v-else></td>
                                    </template>

                                    <template v-else-if="!row.session">
                                        <td style="width: 10px;">
                                            <b-tag type="is-success" style="width: 100%; justify-content: left;"><i class="fas fa-check fa-fw mr-2"></i><strong class="has-text-white">{{ row.total_minutes }}</strong> Minutes</b-tag>
                                        </td>
                                        <td>
                                            <div class="log-title">{{ row.obj.title.replace("-", "").trim() }}</div>
                                            <div class="is-size-7 mt-2 has-text-grey"><i class="far fa-fw fa-clipboard-medical mr-2"></i>You logged this session manually.</div>
                                            <div class="log-date">{{ displayDates(row.obj.start_at, row.obj.end_at) }}</div>
                                        </td>

                                        <td class="has-text-right" style="width: 10px;">
                                            <b-button expanded outlined type="is-warning" @click="removeLog(row.obj)" :disabled="loading || stateRegistration.isLocked">
                                                <i class="fas fa-fw fa-times mr-2"></i>Remove Entry
                                            </b-button>
                                        </td>
                                    </template>

                                    <template v-else>
                                        <td style="width: 10px;">
                                            <b-tag type="is-success" style="width: 100%; justify-content: left;"><i class="fas fa-check fa-fw mr-2"></i><strong class="has-text-white">{{ row.total_minutes }}</strong> Minutes</b-tag>
                                        </td>
                                        <td>
                                            <div class="log-title">{{ row.obj.title }}</div>
                                            <div class="is-size-7 mt-2 has-text-grey"><i class="far fa-fw fa-screen-users mr-2"></i>You attended this in a live session.</div>
                                            <div class="log-date">{{ displayDates(row.obj.start_at, row.obj.end_at) }}</div>
                                        </td>
                                        <td class="has-text-right" style="width: 10px;">
                                            <b-button expanded outlined type="is-warning" @click="removeLog(row.obj)" :disabled="loading || stateRegistration.isLocked">
                                                <i class="fas fa-fw fa-times mr-2"></i>Remove Entry
                                            </b-button>
                                        </td>
                                    </template>

                                </tr>
                                <tr v-if="row.type === 'session'" class="log-row">
                                    <td style="width: 10px;">
                                        <b-tag><i class="far fa-ban has-text-grey-light fa-fw mr-2"></i>Unconfirmed</b-tag>
                                    </td>
                                    <td>
                                        <div class="log-title">{{ row.obj.title }}</div>
                                        <div v-if="!isConcluded(row.obj)" class="is-size-7 mt-2"><i class="far fa-exclamation-triangle mr-2 has-text-warning"></i>Session has not yet concluded. Return after to confirm attendance.</div>
                                        <div v-else class="is-size-7 mt-2"><i class="far fa-arrow-right mr-2 has-text-warning"></i>This session is on your schedule. Confirm that you attended to record it's time.</div>
                                        <div class="log-date">{{ displayDates(row.obj.start_at, row.obj.end_at) }}</div>
                                    </td>
                                    <td class="has-text-right" style="width: 10px;">
                                        <b-button
                                                type="is-primary"
                                                @click="logSession(row.obj)"
                                                :disabled="isAlreadyLogged(row.obj.id) || !isConcluded(row.obj) || loading || stateRegistration.isLocked"
                                        >
                                            <i class="fas fa-fw fa-check mr-2"></i>Confirm Attendance
                                        </b-button>
                                    </td>
                                </tr>
                            </template>
                        </table>

                        <div v-if="logBuilder.length === 0" class="mt-3 pr-6">
                            <div>
                                <i class="far fa-fw fa-arrow-right mr-2"></i>Your log is empty. Add sessions from the <strong><i class="far fa-calendar-circle-user mr-2 ml-1"></i>Sessions & Schedules</strong> or watch <strong><i class="far fa-clapperboard-play mr-2 ml-1"></i>On-Demand Content</strong> to contribute to the log.
                            </div>
                        </div>
                    </section>
                </ui-card>
            </div>
        </div>
    </div>
</template>

<script>
    import {client as http, apiUrl} from "../../http_client";
    import moment from "moment";
    import UiCard from "../../components/UiCard";
    import vueSignature from "vue-signature";
    import {mapGetters} from "vuex";
    import LogSignature from "./LogSignature";

    export default {
        components: {LogSignature, UiCard, vueSignature},
        props: ['registrationId'],
        data: function() {
            return {
                registration: {
                    conference: {}
                },
                logBuilder: [],
                ready: {
                    sessions: false,
                    ondemand: false,
                    log: false,
                    confirm: false
                },
                eval: {
                    q1: null,
                    q2: null,
                    q3: null,
                    q4: null,
                    q5: null,
                    comments: null
                },
                disabled: false,
                sig: null,
                totalMinutes: 0,
                totalClockHours: '0.00',
                apiUrl,
                loading: false

            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            ...mapGetters({
                stateRegistration: 'registration' // TODO temp fix
            }),
            isPreRegistration: function() {
                // return moment().isBefore(moment(this.registration.conference.start_at));
                return false;
            },
            applyReady: function() {
                return this.ready.sessions && this.ready.ondemand && this.ready.log && this.ready.confirm;
            }
        },
        methods: {
            loadRegistration: function(force) {
                http.get('app/registrations/' + this.registrationId, {force}).then(response => {
                    this.registration = response.data;
                    this.buildBuilder();
                });
            },
            buildBuilder: function() {
                this.logBuilder = [];
                this.totalMinutes = 0;
                this.totalClockHours = '0.00';

                // Add all scheduled sessions to the log builder if they aren't in the record log
                for (let i = 0; i < this.registration.reg_sessions.length; i++) {
                    const session = this.registration.reg_sessions[i];
                    session.clock_hours = (session.log_minutes / 60).toFixed(2);

                    if (!this.isAlreadyLogged(session.id)) {
                        this.logBuilder.push({
                            start_at: session.start_at,
                            type: 'session',
                            obj: session,
                            clock_hours: session.clock_hours
                        });
                    }
                }

                this.registration.reg_record_log.sort((a, b) => {
                    return moment(a.start_at).isBefore(moment(b.start_at)) ? -1 : 1;
                });

                let entries = {};
                for (let i = 0; i < this.registration.reg_record_log.length; i++) {
                    const entry = this.registration.reg_record_log[i];

                    if (entry.session_id) {
                        if (entry.minutes > 0) {
                            if (!entries[entry.session_id]) {
                                entries[entry.session_id] = {
                                    start_at: entry.start_at,
                                    type: 'entry',
                                    obj: entry,
                                    children: [],
                                    showRecords: false
                                };
                            }
                            entries[entry.session_id].children.push({...entry});
                        }
                    } else {
                        entries[entry.id] = {
                            start_at: entry.start_at,
                            type: 'entry',
                            obj: entry,
                            children: [entry],
                            showRecords: false
                        };
                    }
                }

                // console.log(entries);

                for (let key in entries) {
                    if (entries.hasOwnProperty(key)) {
                        const entry = entries[key];

                        entry.session = this.getSessionById(entry.obj.session_id);
                        entry.clock_hours = (entry.minutes / 60).toFixed(2);  //TODO this fails for live sessions.  Not sure its even needed.

                        // add up minutes of children objects
                        let minutes = 0;
                        for (let j = 0; j < entry.children.length; j++) {
                            minutes += entry.children[j].minutes;
                        }

                        if (entry.session && entry.session.max_log_minutes > 0 && minutes >= entry.session.max_log_minutes) {
                            minutes = entry.session.max_log_minutes;
                            entry.hit_max = true;
                        }
                        entry.total_minutes = minutes;
                        entry.total_clock_hours = (minutes / 60).toFixed(2);

                        this.totalMinutes += minutes;

                        this.logBuilder.push(entry);
                    }
                }

                this.logBuilder.sort((a, b) => {
                    return new Date(b.start_at) - new Date(a.start_at);
                });
                this.logBuilder.reverse();

                // console.log(this.logBuilder);

                this.totalClockHours = (this.totalMinutes / 60).toFixed(2);
            },
            logSession: function(session) {

                if (!this.loading) {
                    this.loading = true;
                    console.log(session);

                    http.post('app/registrations/' + this.registrationId + '/log', {
                        title: session.title,
                        session_id: session.id,
                        start_at: session.start_at,
                        end_at: session.end_at,
                        minutes: session.log_minutes
                    }).then(response => {
                        this.registration.reg_record_log.push(response.data);
                        this.registration.reg_log_session_ids.push(session.id);
                        this.buildBuilder();
                        this.loading = false;
                    });
                }
            },
            removeLog(entry) {
                if (!this.loading && confirm('Are you sure you want to remove this log entry? This is a permanent action and time from this entry will be lost.')) {
                    this.loading = true;

                    http.delete('app/registrations/' + this.registration.id + '/log/' + entry.id).then(response => {
                        this.registration.reg_record_log.splice(this.registration.reg_record_log.indexOf(entry), 1);
                        this.registration.reg_log_session_ids = this.registration.reg_record_log.map(r => r.session_id);
                        this.buildBuilder();
                        this.loading = false;
                    });
                }
            },
            removeChildLog(parent, entry) {
                if (!this.loading && confirm('Are you sure you want to remove this log entry? This is a permanent action and time from this entry will be lost.')) {
                    this.loading = true;

                    http.delete('app/registrations/' + this.registration.id + '/log/' + entry.id).then(response => {
                        for (let i = 0; i < this.registration.reg_record_log.length; i++) {
                            const e = this.registration.reg_record_log[i];
                            if (e.id === entry.id) {
                                this.registration.reg_record_log.splice(i, 1);
                                break;
                            }
                        }
                        this.registration.reg_log_session_ids = this.registration.reg_record_log.map(r => r.session_id);

                        this.buildBuilder();
                        this.loading = false;
                    });
                }
            },
            getSessionById(id) {
                for (let i = 0; i < this.registration.conference.sessions.length; i++) {
                    const session = this.registration.conference.sessions[i];
                    if (session.id === id) {
                        return session;
                    }
                }
            },
            isAlreadyLogged(session_id) {
                return (this.registration.reg_log_session_ids.indexOf(session_id) !== -1);
            },
            isConcluded(session) {
                return moment(session.end_at).isBefore(moment());
            },
            displayDates: function(start, end) {
                let s = moment(start);
                let e = moment(end);
                if (s.isSame(e, 'day')) {
                    return s.format('dddd, MMMM Do, YYYY, h:mm A') + ' - ' + e.format('h:mm A');
                } else {
                    return s.format('MMM Do, YYYY, h:mm A') + ' - ' + e.format('MMM Do, YYYY, h:mm A');
                }
            },
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                }
            },
            convertMinutesToHHMM(minutes) {
                let hours = Math.floor(minutes / 60);
                let mins = Math.floor(minutes % 60);

                let formattedHours = hours < 10 ? "0" + hours : hours;
                let formattedMins = mins < 10 ? "0" + mins : mins;

                return formattedHours + ":" + formattedMins;
            }
        },
        mounted() {
            this.loadRegistration(true);
        }
    };

</script>

<style lang="scss" scoped>

    .log-row {

        .log-title {
            margin-top: 2px;
            font-size: 1em;
            font-weight: bold;
        }

        .log-date {
            margin-top: 5px;
            font-size: 0.8em;
        }

        td {
            border-color: #eaeaea;
            padding: 15px 10px;
        }

        &:last-child td {
            border: none;
        }
    }

    h4 {
        font-size: 1.2rem;
        margin-top: 1.5rem;
        margin-bottom: 2rem;
        padding-bottom: 0.8rem;

        i {
            color: #336699;
        }

        border-bottom: 1px dashed #ddd;
    }

</style>

<style lang="scss">
    .step-item {
        list-style: none;
    }

    .survey {
        .survey-radio {
            margin: 0 !important;
            padding: 0 !important;
        }
    }

    .session-progress .progress {
        height: 0.25rem !important;
    }
</style>
